import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';

import { PageContentDetails } from 'enrollment/components/PageContentDetails';
import { ROUTES } from 'enrollment/app/routes';
import { formatDescription } from 'enrollment/features/Benefits/BenefitsPreview/GroupedBenefits/utils';
import { BenefitCardProps } from 'enrollment/features/Benefits/BenefitsPreview/types';
import {
  BenefitsPreviewContainerV2,
  BenefitsPreviewPageContentV2,
  PageContentWrapperV2,
} from '../CategoriesV2/CategoriesV2ContainerStyled';
import { PsButtonTuner } from 'stencilSetup';
import { getPremiumStyled, RETURN_VALUE_ONLY } from 'enrollment/utils';
import { selectPerPayPeriodText } from 'enrollment/services';
import { GET_BENEFIT_PLANS_QUERY_KEY } from 'enrollment/queries/useBenefitPlansQuery/utils';
import { queryClient } from 'theSource/utils';


interface UnselectedBenefitProps extends BenefitCardProps {
  plans?: string[];
  onMount?: (id: string) => void;
}

export const UnselectedBenefitV2 = ({
  benefitData,
  plans,
  contextLookup,
  navigatePath,
  currentCategory,
  pendedBenefitData,
  onMount,
}: UnselectedBenefitProps) => {
  const { t } = useTranslation('enrollment.plugins');
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const perPayText = useSelector(selectPerPayPeriodText);

  const pluginBanner = benefitData.benefit.benefit_plugin ? true : false;
  // const bannerText = t('unelectedBanner');

  const navigateToPlans = () => {
    //used for benefit plans. this invalidates all plan queries matching this including those with and without dependents - needed if dependent plan viewed out of order
    queryClient.invalidateQueries({
      queryKey: [
        GET_BENEFIT_PLANS_QUERY_KEY + '-' + benefitData.benefit.org_benefit_id,
      ],
    });
    let nav = navigatePath || `/enrollment/${ROUTES.enrollment.plans.path}`;
    const isSurvey = benefitData.benefit.benefit_class === 'survey';

    if (pluginBanner) {
      nav = `/enrollment/${ROUTES.enrollment.plugin.path}/${benefitData.benefit.benefit_plugin!.code}`;
    } else if (isSurvey) {
      nav = `/enrollment/survey/${benefitData.benefit.org_benefit_id}`;
    }

    navigate(nav, {
      state: {
        benefitData: benefitData,
        pendedBenefitData: pendedBenefitData,
        contextLookup: contextLookup,
        currentBenefitCategory: currentCategory,
        currentCategory: currentCategory,
        benefitId: isSurvey ? benefitData.benefit.org_benefit_id : undefined,
      },
    });
  };

  const orgPlanYear = benefitData.benefit.org_plan_year_id;
  const benefitLookup = benefitData.benefit.benefit_lookup_code;
  const benefitName = benefitData.benefit.org_benefit_name;
  const description = formatDescription(
    benefitData.benefit.page_content_summary
  );
  const lookupCode = benefitData.benefit.benefit_lookup_code;

  useEffect(() => {
    if (ref.current && onMount) {
      onMount(lookupCode);
    }
  }, [onMount, lookupCode]);

  return (
    <BenefitsPreviewContainerV2 className="preview-item-v2" id={lookupCode} ref={ref}>
      <div>
        <h3 id={benefitLookup}>
          <span id={benefitName}>{benefitName}</span> -{' '}
          <span className="body1"> {t('noCoverage')}</span>
        </h3>

        {description && description.length > 0 && (
          <>
            <BenefitsPreviewPageContentV2>
              {description}
            </BenefitsPreviewPageContentV2>
            <PageContentWrapperV2>
              <PageContentDetails
                pageContentLookup="ENROLL_COVERAGE"
                orgPlanYear={orgPlanYear}
                benefitLookup={benefitLookup}
                orgPlans={plans}
                benefitName={benefitName}
              />
            </PageContentWrapperV2>
          </>
        )}
      </div>
      <PsButtonTuner
        buttonId={`plan-preview-${benefitData.benefit.org_benefit_id}`}
        label={t('explore')}
        level="primary"
        onClick={() => navigateToPlans()}
        otherData={[{ attribute: 'aria-describedby', value: benefitName }]}
      />
      <div className="d-flex flex-column align-items-end">
        <div className="mb-0 display1">
          {getPremiumStyled({
            premium: '0.00',
            valueOnly: RETURN_VALUE_ONLY,
          })}
        </div>
        <p className="body2 mb-0">{perPayText}</p>
      </div>
    </BenefitsPreviewContainerV2>
  );
};
